import { CustomError, RequiredDocumentWithStatus } from '@applyonline/shared'
import { ScrollableAlert } from '@applyonline/shared/components'
import { replaceLastSpaceWithNbsp } from '@applyonline/shared/utils'
import { EDocumentStatus } from '@if/core/types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RecaptchaUnavailableError } from '../services/recaptcha'
import UploadFileButton from './UploadFileButton'

interface Props {
  creditApplicationId: string
  applicant: string
  requiredDocuments: RequiredDocumentWithStatus[]
}

const RequiredDocumentTable = ({ requiredDocuments, applicant, creditApplicationId }: Props) => {
  const { t, i18n } = useTranslation()
  const [error, setError] = useState<CustomError>()
  const [uploadError, setUploadError] = useState<Error | null>(null)
  const isRecaptchaUnavailable = uploadError instanceof RecaptchaUnavailableError

  const getStatusClass = (status: EDocumentStatus) => {
    switch (status) {
      case EDocumentStatus.AwaitingDocument:
        return 'text-statusBlue'
      case EDocumentStatus.AwaitingApproval:
        return 'text-statusYellow'
      case EDocumentStatus.Incomplete:
        return 'text-statusYellow'
      case EDocumentStatus.Refused:
        return 'text-statusRed'
      case EDocumentStatus.Approved:
        return 'text-statusGreen'
      default:
        return ''
    }
  }

  const hasRefusedDocument = requiredDocuments.some((doc) => doc.status === EDocumentStatus.Refused)

  const hasIncompletedDocument = requiredDocuments.some((doc) => doc.status === EDocumentStatus.Incomplete)

  return (
    <div className="scroll-container">
      <h3> {applicant && `${applicant.charAt(0).toUpperCase()}${applicant.slice(1)}`}</h3>
      <ScrollableAlert
        showAlert={isRecaptchaUnavailable}
        type="error"
        style={{ color: 'white', marginTop: '15px' }}
        message={<span style={{ color: 'white' }}>{t('common.errors.recaptchaUnavailable')}</span>}
      />
      <table className="styled-table" style={isRecaptchaUnavailable ? { marginTop: 0 } : {}}>
        <thead>
          <tr>
            <th>{t('uploadRequiredFile.documentName')}</th>
            <th>{t('uploadRequiredFile.status')}</th>
            <th>{t('uploadRequiredFile.upload')}</th>
          </tr>
        </thead>
        <tbody>
          {requiredDocuments.map((doc) => (
            <tr key={doc.id}>
              <td data-label={t('common.document')}>
                {i18n.language === 'fr' ? replaceLastSpaceWithNbsp(doc.nameFr) : replaceLastSpaceWithNbsp(doc.nameEn)}

                <br />
                <span className="table-note">{doc.note ?? ''}</span>
              </td>
              <td className="fixed-width-250" data-label={t('uploadRequiredFile.status')}>
                {error && error.id === doc.id ? (
                  <span className={getStatusClass(EDocumentStatus.Refused)}>
                    {t(`EDocumentStatus.${EDocumentStatus.Refused}`, { ns: 'enum' })}: {error.message}
                  </span>
                ) : (
                  <span className={getStatusClass(doc.status)}>
                    {t(`EDocumentStatus.${doc.status}`, { ns: 'enum' })}
                  </span>
                )}
                {(hasRefusedDocument || hasIncompletedDocument) &&
                  (window.innerWidth > 600 ? (
                    <>
                      <br />
                      <span
                        className="table-note"
                        style={{ color: '#E97472', textAlign: 'justify', paddingRight: '10px' }}
                      >
                        {(doc.status === EDocumentStatus.Refused || doc.status === EDocumentStatus.Incomplete) &&
                          doc.refusalReason}
                      </span>
                    </>
                  ) : (
                    doc.refusalReason !== null && (
                      <>
                        <br />
                        <span
                          className="table-note"
                          style={{
                            color: '#E97472',
                            textAlign: 'justify',
                          }}
                        >
                          {(doc.status === EDocumentStatus.Refused || doc.status === EDocumentStatus.Incomplete) &&
                            doc.refusalReason}
                        </span>
                      </>
                    )
                  ))}
              </td>
              <td data-label={t('uploadRequiredFile.upload')}>
                <UploadFileButton
                  id={doc.id}
                  creditApplicationId={creditApplicationId}
                  typeId={doc.typeId}
                  subKey={doc.subKey}
                  applicantType={doc.applicantType}
                  disabled={doc.status === EDocumentStatus.Approved}
                  setError={setError}
                  setUploadError={setUploadError}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
export default RequiredDocumentTable
